/**
 * Item versions (migration 912) — what an item said each time it changed, and
 * which version each ranking used.
 *
 * Versions are written only by database triggers on items and item_frames, and
 * a session result is stamped with the item's version when it is written. This
 * module only reads. Design: docs/9-exploration/PRD-item-versions.md.
 */
import { getTypedSupabaseClient } from './typedSupabaseClient';

export interface ItemVersion {
  id: string;
  version: number;
  title: string;
  description: string | null;
  itemCategory: string | null;
  altitudeLevel: number | null;
  /** Plain words for what changed from the previous version; empty on version 1. */
  changed: string[];
  createdAt: string;
}

export interface ItemRankedVersion {
  resultId: string;
  sessionId: string;
  sessionName: string;
  cycleId: string | null;
  cycleName: string | null;
  rank: number;
  resultStatus: string;
  rankedAt: string;
  /** Null for results written before versions were recorded (migration 912). */
  rankedVersion: number | null;
  rankedTitle: string | null;
  currentVersion: number | null;
  changedSinceRanked: boolean;
  /** Groups in this ranking's cycle ranked different versions, and nobody has settled it yet. */
  cycleHasMultipleVersions: boolean;
  /** False when the cycle's settlement made this ranking informational (928). */
  counts: boolean;
  /** How the flag in this ranking's cycle was settled, if it was. */
  settlement: VersionSettlement | null;
}

export type VersionSettlementChoice = 'no_difference' | 'rerank_newest' | 'version_counts';

export interface VersionSettlement {
  choice: VersionSettlementChoice;
  /** The version that counts (version_counts) or is to be re-ranked on (rerank_newest). */
  countedVersion: number | null;
  reason: string;
  settledBy: string | null;
  settledAt: string;
}

/** The three ways to settle, in the owner's words (PRD-item-versions §5). */
export const VERSION_SETTLEMENT_CHOICES: { value: VersionSettlementChoice; label: string; description: string }[] = [
  { value: 'no_difference', label: 'The differences don\u2019t matter', description: 'The wording changed but not the substance. Every ranking counts.' },
  { value: 'rerank_newest', label: 'Re-rank on the newest version', description: 'Send the newest version back to the groups that ranked an older one. Their rankings stop counting until they rank it again.' },
  { value: 'version_counts', label: 'Choose the version that counts', description: 'Rankings on other versions stay visible as informational.' },
];

export async function fetchItemVersions(itemId: string): Promise<ItemVersion[]> {
  const { data, error } = await getTypedSupabaseClient()
    .from('item_versions')
    .select('id, version, title, description, item_category, altitude_level, changed, created_at')
    .eq('item_id', itemId)
    .order('version', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => ({
    id: r.id,
    version: r.version,
    title: r.title,
    description: r.description,
    itemCategory: r.item_category,
    altitudeLevel: r.altitude_level,
    changed: r.changed ?? [],
    createdAt: r.created_at,
  }));
}

export async function fetchItemRankedVersions(itemId: string): Promise<ItemRankedVersion[]> {
  const { data, error } = await getTypedSupabaseClient().rpc('get_item_ranked_versions', { p_item_id: itemId });
  if (error) throw new Error(error.message);
  return (data ?? []).map((r) => ({
    resultId: r.result_id,
    sessionId: r.session_id,
    sessionName: r.session_name,
    cycleId: r.cycle_id ?? null,
    cycleName: r.cycle_name ?? null,
    rank: r.rank,
    resultStatus: r.result_status,
    rankedAt: r.ranked_at,
    rankedVersion: r.ranked_version ?? null,
    rankedTitle: r.ranked_title ?? null,
    currentVersion: r.current_version ?? null,
    changedSinceRanked: !!r.changed_since_ranked,
    cycleHasMultipleVersions: !!r.cycle_has_multiple_versions,
    ...settlementOf(r as SettlementColumns),
  }));
}

// Columns added by migration 928; not in the generated types until db:types runs after it is applied.
interface SettlementColumns {
  counts?: boolean | null;
  settlement_choice?: VersionSettlementChoice | null;
  settlement_counted_version?: number | null;
  settlement_reason?: string | null;
  settled_by?: string | null;
  settled_at?: string | null;
}

function settlementOf(r: SettlementColumns): { counts: boolean; settlement: VersionSettlement | null } {
  return {
    counts: r.counts ?? true,
    settlement: r.settlement_choice
      ? {
          choice: r.settlement_choice,
          countedVersion: r.settlement_counted_version ?? null,
          reason: r.settlement_reason ?? '',
          settledBy: r.settled_by ?? null,
          settledAt: r.settled_at ?? '',
        }
      : null,
  };
}

type LooseRpc = (fn: string, args: object) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
const looseRpc: LooseRpc = (fn, args) => (getTypedSupabaseClient().rpc as unknown as LooseRpc)(fn, args);

/** May the signed-in person settle version flags in this cycle (R2)? False when it cannot be read. */
export async function canSettleItemVersionFlag(cycleId: string): Promise<boolean> {
  const { data, error } = await looseRpc('can_settle_item_version_flag', { p_cycle_id: cycleId });
  return !error && data === true;
}

/** Settle a flag. Returns an error message, or null. */
export async function settleItemVersionFlag(args: {
  cycleId: string;
  itemId: string;
  choice: VersionSettlementChoice;
  countedVersion: number | null;
  reason: string;
}): Promise<string | null> {
  const { error } = await looseRpc('settle_item_version_flag', {
    p_cycle_id: args.cycleId,
    p_item_id: args.itemId,
    p_choice: args.choice,
    p_counted_version: args.countedVersion,
    p_reason: args.reason,
  });
  return error ? error.message : null;
}

export interface CycleVersionFlag {
  itemId: string;
  itemTitle: string;
  versions: number[];
  settled: boolean;
  settlementChoice: VersionSettlementChoice | null;
}

/** Items in a cycle that groups ranked on different versions. Null when it could not be read. */
export async function fetchCycleVersionFlags(cycleId: string): Promise<CycleVersionFlag[] | null> {
  const { data, error } = await looseRpc('get_cycle_version_flags', { p_cycle_id: cycleId });
  if (error || !Array.isArray(data)) return null;
  return (data as { item_id: string; item_title: string; versions: number[]; settled: boolean; settlement_choice: VersionSettlementChoice | null }[]).map((r) => ({
    itemId: r.item_id,
    itemTitle: r.item_title,
    versions: r.versions ?? [],
    settled: !!r.settled,
    settlementChoice: r.settlement_choice ?? null,
  }));
}

export interface ItemLockState {
  /** True once any version of the item has been ranked (migration 913). Title and description answer to this. */
  locked: boolean;
  /** True for a workspace owner/admin or someone with decision authority. */
  canChange: boolean;
  /** The plan in force that holds the item, with no cycle named (1019). */
  planLock: { planCycleId: string; planName: string; cycleId: string | null } | null;
}

export async function fetchItemLockState(itemId: string): Promise<ItemLockState | null> {
  const { data, error } = await getTypedSupabaseClient().rpc('get_item_lock_state', { p_item_id: itemId });
  if (error) throw new Error(error.message);
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  const d = data as {
    locked?: boolean;
    can_change?: boolean;
    plan_lock?: { plan_cycle_id: string; plan_name: string; cycle_id: string | null } | null;
  };
  return {
    locked: !!d.locked,
    canChange: !!d.can_change,
    planLock: d.plan_lock ? { planCycleId: d.plan_lock.plan_cycle_id, planName: d.plan_lock.plan_name, cycleId: d.plan_lock.cycle_id } : null,
  };
}

export interface ItemWording {
  title: string;
  description: string | null;
  version: number;
}

/**
 * What each item said at a moment: its latest version created at or before `at`
 * (migration 912). Owner ruling R4: a session ranks the version current when it
 * opened for ranking, so its screens show that wording, not a later edit. Items
 * with no version by then are left out — callers keep today's wording for them.
 */
export async function fetchItemWordingAsOf(itemIds: string[], at: string | Date): Promise<Map<string, ItemWording>> {
  const ids = [...new Set(itemIds)].filter(Boolean);
  const out = new Map<string, ItemWording>();
  if (ids.length === 0) return out;
  const iso = typeof at === 'string' ? at : at.toISOString();
  const { data, error } = await getTypedSupabaseClient()
    .from('item_versions')
    .select('item_id, version, title, description')
    .in('item_id', ids)
    .lte('created_at', iso)
    .order('version', { ascending: false });
  if (error || !data) return out;
  for (const r of data) {
    if (!out.has(r.item_id)) out.set(r.item_id, { title: r.title, description: r.description, version: r.version });
  }
  return out;
}
